import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useState, useRef, useEffect } from "react";
import { FaCheckCircle, FaUserTie, FaGlobe, FaTools, FaChartLine, FaPhoneAlt, FaSearch, FaChevronDown } from "react-icons/fa";
import "../Styles/EuropeVisa.css";
import Reveal, { RevealGroup, RevealItem } from "./Reveal";
import MagneticButton from "./MagneticButton";

const countries = [
  { flag: "🇩🇪", name: "Germany", desc: "EU Blue Card, Job Seeker Visa. Engineering, IT & healthcare in demand. Clear PR pathway via permanent settlement." },
  { flag: "🇸🇪", name: "Sweden", desc: "Skilled worker routes with strong worker rights. High salaries and excellent work-life balance." },
  { flag: "🇳🇱", name: "Netherlands", desc: "Highly skilled migrant visa. Top tech & finance hub. English widely spoken, vibrant expat community." },
  { flag: "🇮🇪", name: "Ireland", desc: "Critical Skills Employment Permit. Gateway to EU. Booming tech and pharma sectors." },
  { flag: "🇵🇱", name: "Poland", desc: "One of the fastest growing EU economies. Strong demand for IT professionals and engineers." },
  { flag: "🇲🇹", name: "Malta", desc: "English-speaking EU member. Digital nomad visas and affordable cost of living in the Mediterranean." },
];

const visaOptions = [
  "Skilled Worker Visa",
  "Job Seeker Visa",
  "EU Blue Card",
  "Seasonal Work Visa",
  "Permanent Residency Programs",
];

const whoCanApply = [
  "Skilled professionals (IT, healthcare, engineering, etc.)",
  "Fresh graduates (selected programs)",
  "Individuals seeking long-term settlement",
  "Applicants with basic English proficiency",
];

const services = [
  "Profile evaluation & eligibility check",
  "Resume building (European format)",
  "Job search assistance",
  "Interview preparation",
  "Visa documentation & filing",
  "Post-landing support",
];

const steps = [
  { num: 1, title: "Register & Get Assessed", desc: "Submit your profile for a free eligibility evaluation" },
  { num: 2, title: "Choose Country & Program", desc: "We match you with the best European country and visa route" },
  { num: 3, title: "Apply for Jobs / Visa", desc: "We assist with job applications and visa documentation" },
  { num: 4, title: "Get Visa Approval", desc: "We handle follow-ups and ensure a smooth approval process" },
  { num: 5, title: "Fly & Start Your New Life", desc: "Post-landing support to help you settle in Europe" },
  { num: 6, title: "Post-Landing Support", desc: "Guidance on accommodation, banking, and local registration" },
];

const europeCountries = [
  "Germany", "Sweden", "Netherlands", "Ireland", "Poland", "Malta",
  "France", "Spain", "Italy", "Portugal", "Belgium", "Austria",
  "Switzerland", "Denmark", "Norway", "Finland", "Czech Republic",
  "Hungary", "Romania", "Greece", "Croatia", "Luxembourg",
];

function SearchableDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = europeCountries.filter(c => c.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="europe-dropdown" ref={ref}>
      <div className="europe-dropdown-trigger" onClick={() => setOpen(!open)}>
        <span>{value || "Select Desired Country"}</span>
        <FaChevronDown className={`europe-dropdown-arrow ${open ? "open" : ""}`} />
      </div>
      {open && (
        <div className="europe-dropdown-menu">
          <div className="europe-dropdown-search">
            <FaSearch className="europe-search-icon" />
            <input
              autoFocus
              type="text"
              placeholder="Search country..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="europe-dropdown-list">
            {filtered.map((c, i) => (
              <div key={i} className={`europe-dropdown-item ${value === c ? "selected" : ""}`}
                onClick={() => { onChange(c); setOpen(false); setSearch(""); }}>
                {c}
              </div>
            ))}
            {filtered.length === 0 && <div className="europe-dropdown-empty">No results</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EuropeVisa() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", country: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${process.env.REACT_APP_API_URL || "https://veabroad.com"}/api/europeForm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, phone: form.phone, city: form.city, country: form.country, category: "Europe Visa", source: "Europe visa page" }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
  };

  return (
    <div className="europe-page">
      <Helmet>
        <title>Europe Work & Settlement Visa | Settle in Germany, Sweden, NL</title>
        <meta name="description" content="Live and work in Europe. Get expert assistance with EU Blue Cards, Job Seeker Visas, and Skilled Worker routes for Germany, Sweden, Netherlands & more." />
        <link rel="canonical" href="https://veabroad.com/europe-visa" />
        <meta property="og:title" content="Europe Work & Settlement Visa | Settle in Germany, Sweden, NL" />
        <meta property="og:description" content="Live and work in Europe. Get expert assistance with EU Blue Cards, Job Seeker Visas, and Skilled Worker routes for Germany, Sweden, Netherlands & more." />
        <meta property="og:url" content="https://veabroad.com/europe-visa" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Europe Work & Settlement Visa | Settle in Germany, Sweden, NL" />
        <meta name="twitter:description" content="Live and work in Europe. Get expert assistance with EU Blue Cards, Job Seeker Visas, and Skilled Worker routes for Germany, Sweden, Netherlands & more." />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Europe Work & Settle Visa Support",
            "provider": {
              "@type": "EducationalOrganization",
              "name": "Vidya Education Abroad",
              "url": "https://veabroad.com/"
            },
            "areaServed": "IN",
            "description": "Comprehensive pathways and documentation support for relocating and working in European countries."
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="europe-hero">
        <div className="europe-hero-overlay" />
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-mint" style={{ top: '5%', left: '8%', width: '320px', height: '320px' }} />
          <div className="va-blob va-blob-teal" style={{ bottom: '0%', right: '5%', width: '380px', height: '380px' }} />
        </div>
        <div className="containerEurope europe-hero-inner">
          <Reveal as="div" direction="left" className="europe-hero-text">
            <div className="europe-hero-badge">🌍 Europe Work & Settle Visa</div>
            <h1>Build Your <span className="va-shine-text va-shine-text-on-dark">Future in Europe</span></h1>
            <p>Start your journey toward a successful international career and permanent settlement in Europe. Strong economies, high living standards, and global career opportunities await you.</p>
            <div className="europe-hero-points">
              <span><FaCheckCircle /> High-paying jobs</span>
              <span><FaCheckCircle /> PR pathway</span>
              <span><FaCheckCircle /> 20+ countries</span>
            </div>
          </Reveal>
          <Reveal as="div" direction="right" delay={0.15} className="europe-hero-form-wrap">
            {submitted ? (
              <div className="europe-form-success">
                <div className="europe-form-success-icon">✅</div>
                <h3>Thank You!</h3>
                <p>We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form className="europe-hero-form" onSubmit={handleSubmit}>
                <h3>Get Free Consultation</h3>
                <input type="text" placeholder="Full Name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                <input type="tel" placeholder="Mobile Number" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                <input type="email" placeholder="Email Address" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                <input type="text" placeholder="Your City" required value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
                <SearchableDropdown value={form.country} onChange={c => setForm({...form, country: c})} />
                <MagneticButton as="span" className="w-100">
                  <button type="submit" className="europe-form-submit va-shimmer-btn">Submit Enquiry</button>
                </MagneticButton>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      {/* WHY EUROPE */}
      <section className="europe-section">
        <div className="container">
          <Reveal as="h2"><FaGlobe className="europe-section-icon" /> Why Choose Europe?</Reveal>
          <RevealGroup as="div" className="europe-benefits-grid" stagger={0.08}>
            {[
              "High-paying job opportunities across multiple industries",
              "Work-life balance and employee benefits",
              "Pathway to permanent residency and citizenship",
              "World-class healthcare and education",
              "Safe, stable, and multicultural environment",
              "Globally recognised degrees and work experience",
            ].map((b, i) => (
              <RevealItem as="div" key={i} className="europe-benefit-item va-hover-lift">
                <FaCheckCircle className="europe-check" />
                <span>{b}</span>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* COUNTRIES */}
      <section className="europe-section europe-light">
        <div className="container">
          <Reveal as="h2">💼 Countries We Cover</Reveal>
          <Reveal as="p" delay={0.1} className="europe-subtitle">We assist with work and settlement opportunities in top European countries</Reveal>
          <RevealGroup as="div" className="europe-countries-grid" stagger={0.06}>
            {countries.map((c, i) => (
              <RevealItem as="div" key={i} direction="zoom" className="europe-country-card">
                <div className="europe-country-card-inner">
                  <div className="europe-country-front">
                    <span className="europe-flag">{c.flag}</span>
                    <span className="europe-country-name">{c.name}</span>
                  </div>
                  <div className="europe-country-back">
                    <h5>{c.name}</h5>
                    <p>{c.desc}</p>
                  </div>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* VISA OPTIONS + WHO CAN APPLY */}
      <section className="europe-section">
        <div className="container">
          <RevealGroup as="div" className="row g-4" stagger={0.15}>
            <RevealItem as="div" direction="left" className="col-lg-6">
              <div className="europe-card h-100 va-hover-lift">
                <h3>📋 Visa Options Available</h3>
                <ul className="europe-list">
                  {visaOptions.map((v, i) => (
                    <li key={i}><FaCheckCircle className="europe-check" />{v}</li>
                  ))}
                </ul>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="right" className="col-lg-6">
              <div className="europe-card h-100 va-hover-lift">
                <h3>🧑‍💻 Who Can Apply?</h3>
                <ul className="europe-list">
                  {whoCanApply.map((w, i) => (
                    <li key={i}><FaCheckCircle className="europe-check" />{w}</li>
                  ))}
                </ul>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* OUR SERVICES */}
      <section className="europe-section europe-light">
        <div className="container">
          <Reveal as="h2"><FaTools className="europe-section-icon" /> Our Services</Reveal>
          <RevealGroup as="div" className="europe-services-grid" stagger={0.08}>
            {services.map((s, i) => (
              <RevealItem as="div" key={i} className="europe-service-item va-hover-lift">
                <FaCheckCircle className="europe-check" />
                <span>{s}</span>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="europe-section">
        <div className="container">
          <Reveal as="h2"><FaChartLine className="europe-section-icon" /> How It Works</Reveal>
          <RevealGroup as="div" className="europe-steps" stagger={0.08}>
            {steps.map((s) => (
              <RevealItem as="div" key={s.num} direction="zoom" className="europe-step">
                <div className="europe-step-num va-float">{s.num}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="europe-section europe-light">
        <div className="container">
          <Reveal as="h2"><FaUserTie className="europe-section-icon" /> Why Choose Us?</Reveal>
          <RevealGroup as="div" className="europe-why-grid" stagger={0.08}>
            {[
              { icon: "🏆", title: "Experienced Consultants", desc: "Years of expertise in European immigration" },
              { icon: "✅", title: "High Success Rate", desc: "Proven track record of visa approvals" },
              { icon: "🔍", title: "Transparent Process", desc: "No hidden fees, clear communication" },
              { icon: "🤝", title: "End-to-End Support", desc: "From profile to post-landing assistance" },
              { icon: "🎯", title: "Personalized Guidance", desc: "Tailored strategy for your unique profile" },
              { icon: "⚡", title: "Fast Processing", desc: "Quick turnaround on applications and documentation" },
            ].map((w, i) => (
              <RevealItem as="div" key={i} className="europe-why-card va-hover-lift">
                <div className="europe-why-icon va-float">{w.icon}</div>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* CTA */}
      <section className="europe-cta-section">
        <div className="container text-center">
          <Reveal direction="zoom">
            <FaPhoneAlt className="europe-cta-icon va-float" />
            <h2>Get Started Today!</h2>
            <p>Don't miss your chance to live and work in Europe.</p>
            <MagneticButton as="span">
              <button className="lmbtn" style={{ border: 'none' }}>
                <Link to="/consultationForm" className="cButton">Book Your Free Consultation Now</Link>
              </button>
            </MagneticButton>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
